import { useState } from "react";
import { api, ApiError } from "../api/client";
import { useAuth } from "../auth/store";
import { deviceClientId } from "../desktop/bridge";
import { Shield, WifiOff } from "lucide-react";
import { useT } from "../i18n";

export function Login({ canOffline, onOffline }: { canOffline?: boolean; onOffline?: () => void } = {}) {
  const setSession = useAuth((s) => s.setSession);
  const t = useT();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // SSO (Microsoft 365 / Entra) is the primary sign-in; name+password is a backup, revealed on demand.
  const [showPw, setShowPw] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(login, password, totp || undefined, await deviceClientId());
      setSession(res.session_token, login);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  // SSO: resolve this install's stable device id first (desktop), then navigate to the OIDC start
  // URL with it, so the SSO session reuses the same device row (keeps offline_enabled, §15).
  async function ssoLogin(e: React.MouseEvent) {
    e.preventDefault();
    window.location.href = api.oidcLoginUrl(await deviceClientId());
  }

  return (
    <div className="flex h-full items-center justify-center bg-bg">
      <div className="w-[360px] rounded-md border border-border bg-panel shadow-xl">
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
          <div className="grid h-8 w-8 place-items-center rounded-md bg-accent text-white"><Shield className="h-4 w-4" /></div>
          <div>
            <div className="font-semibold tracking-tight">Vault &amp; Remote Access</div>
            <div className="text-xs text-muted">{t("Sign in to your workspace")}</div>
          </div>
        </div>
        <div className="flex flex-col gap-3 px-5 py-5">
          {/* Primary sign-in: Microsoft 365 SSO (Entra OIDC). */}
          <a
            href={api.oidcLoginUrl()}
            onClick={ssoLogin}
            className="rounded bg-accent px-3 py-2.5 text-center font-medium text-white hover:bg-accent2"
          >
            {t("Sign in with Microsoft 365")}
          </a>
          <p className="text-center text-xs text-muted">{t("Use your work account.")}</p>

          {!showPw ? (
            <button
              type="button"
              onClick={() => setShowPw(true)}
              className="text-center text-xs text-muted underline-offset-2 hover:text-fg hover:underline"
            >
              {t("Sign in with name and password instead")}
            </button>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              <div className="relative my-1 text-center text-xs text-muted">
                <span className="bg-panel px-2">{t("Backup sign-in")}</span>
                <div className="absolute inset-x-0 top-1/2 -z-10 border-t border-border" />
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted">{t("Login")}</span>
                <input
                  className="rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  autoFocus
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted">{t("Password")}</span>
                <input
                  type="password"
                  className="rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-muted">{t("2FA code (if enabled)")}</span>
                <input
                  inputMode="numeric"
                  className="rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                />
              </label>
              {error && <div className="rounded bg-danger/15 px-2 py-1.5 text-xs text-danger">{error}</div>}
              <button
                type="submit"
                disabled={busy}
                className="mt-1 rounded bg-accent px-3 py-2 font-medium text-white hover:bg-accent2 disabled:opacity-60"
              >
                {busy ? t("Signing in…") : t("Sign in")}
              </button>
            </form>
          )}

          {canOffline && onOffline && (
            <button
              type="button"
              onClick={onOffline}
              className="flex items-center justify-center gap-2 rounded border border-border px-3 py-2 text-center text-muted hover:border-accent hover:text-fg"
            >
              <WifiOff className="h-4 w-4" /> {t("Continue offline")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
